import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  ActorAttribution,
  BlackboardInstance,
  ChannelMessage,
} from "../blackboardModels";
import type { IngestedStreamEvent } from "../useInstanceStream";

import { ChatMessageMarkdown } from "../../common/ChatMessageMarkdown";
import { getAvatarBackgroundColor, InstanceAvatar } from "../InstanceAvatar";
import {
  buildTimelineEntries,
  submitTimelineMessage,
  type TimelineActivityEntry,
  type TimelineEntry,
  type TimelineIdentity,
  type TimelineMessageEntry,
} from "./channelTimelineModel";
import { MentionMenu } from "./MentionMenu";

// ---------------------------------------------------------------------------
// ChannelTimeline — one conversation surface, two materials.
//
// Speech is figural: bubbles anchor the scroll, sender + time lifted out of
// the bubble onto a meta line so the bubble stays pure speech. Telemetry is
// textural: consecutive activity from one actor folds into a TURN CAPSULE —
// one summarizable row (tools · tokens · cost · time range) that expands to
// the full trace. The live in-flight turn streams expanded and folds when the
// actor parks. Everything hangs off one left spine except the operator's own
// messages (right, chat convention) and hairline time rules.
//
// Activity is live-only (WS, 200-cap); message history is full (REST). The
// model inserts an explicit live boundary so the half-record is visible
// instead of implied.
// ---------------------------------------------------------------------------

interface ChannelTimelineProps {
  /** Org-scoped actor identities, including killed actors, for attribution only. */
  actorAttributions: ActorAttribution[];
  appendLocalUserMessage: (message: ChannelMessage) => void;
  channelId: string;
  draft: string;
  events: IngestedStreamEvent[];
  hasMore: boolean;
  liveSince: string | null;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  /** Active channel members available for @ addressing. */
  members: BlackboardInstance[];
  messages: ChannelMessage[];
  onDraftChange: (value: string) => void;
  /** Send a message to the channel and return the persisted message id. */
  onSendMessage: (channelId: string, message: string) => Promise<string>;
  readOnly?: boolean;
}

/** Break a time rule (and any turn capsule) when the gap exceeds this. */
const TIMESTAMP_GAP_MS = 5 * 60 * 1000;

/**
 * An in-flight turn with no new rows for this long is treated as ended —
 * some wakes end without a channel-scoped park/status event, and "working…"
 * must not outlive the work.
 */
const IN_FLIGHT_STALE_MS = 2 * 60 * 1000;

// --- block model -------------------------------------------------------------
// The entry list is regrouped for render: messages and boundaries pass through
// in exact chronological position; activity coalesces into ONE turn capsule
// per actor per conversation beat (the span between two messages/boundaries),
// even when actors' rows genuinely interleave in time. The capsule is an
// abstraction over a turn, so it trades row-level interleave for narrative
// coherence; honesty is preserved by the time range on the capsule and the
// true per-row clocks on expansion. A capsule still closes on its actor's
// turn end or a >gap pause, and never spans a message.

type TimelineBlock =
  | {
      entry: TimelineEntry & { kind: "live-boundary" };
      id: string;
      kind: "boundary";
    }
  | { entry: TimelineMessageEntry; id: string; kind: "message" }
  | {
      endsTurn: boolean;
      id: string;
      identity: TimelineIdentity | null;
      kind: "turn";
      rows: TimelineActivityEntry[];
    };

type TurnBlockShape = Extract<TimelineBlock, { kind: "turn" }>;

function groupTimelineBlocks(entries: TimelineEntry[]): TimelineBlock[] {
  const blocks: TimelineBlock[] = [];
  // Open capsules for the current conversation beat, keyed by owner. Cleared
  // at every message/boundary so no capsule ever spans a conversation anchor.
  let openByOwner = new Map<string | null, TurnBlockShape>();
  for (const entry of entries) {
    if (entry.kind === "message") {
      openByOwner = new Map();
      blocks.push({ entry, id: entry.id, kind: "message" });
      continue;
    }
    if (entry.kind === "live-boundary") {
      openByOwner = new Map();
      blocks.push({ entry, id: entry.id, kind: "boundary" });
      continue;
    }
    const owner = entry.identity?.id ?? null;
    const capsule = openByOwner.get(owner);
    const lastRow = capsule?.rows[capsule.rows.length - 1];
    const withinGap =
      lastRow !== undefined &&
      entry.timestamp - lastRow.timestamp <= TIMESTAMP_GAP_MS;
    // A turn end does NOT close the capsule: one actor gets one line per
    // beat even across park→wake cycles (three consecutive singletons was
    // vertical waste). endsTurn tracks only whether the LAST row ended a
    // turn — it drives the breath spacing and the settled/in-flight state.
    if (capsule && withinGap) {
      capsule.rows.push(entry);
      capsule.endsTurn = entry.isTurnEnd;
    } else {
      const next: TurnBlockShape = {
        endsTurn: entry.isTurnEnd,
        id: entry.id,
        identity: entry.identity,
        kind: "turn",
        rows: [entry],
      };
      openByOwner.set(owner, next);
      blocks.push(next);
    }
  }
  return blocks;
}

interface TurnSummary {
  costUsd: number;
  events: number;
  from: number;
  terminal: string | null;
  to: number;
  tokens: number;
  tools: number;
  wakes: number;
}

function terminalLabel(row: TimelineActivityEntry): string | null {
  if (row.event.type === "activity_signal" && row.event.label === "PARK") {
    return "parked";
  }
  if (row.event.type === "status_change" && row.event.status === "waiting") {
    return "parked";
  }
  if (row.event.type === "status_change" && row.event.status === "killed") {
    return "killed";
  }
  return null;
}

function summarizeTurn(rows: TimelineActivityEntry[]): TurnSummary {
  const lastRow = rows[rows.length - 1];
  const summary: TurnSummary = {
    costUsd: 0,
    events: rows.length,
    from: rows[0]?.timestamp ?? 0,
    terminal: lastRow ? terminalLabel(lastRow) : null,
    to: lastRow?.timestamp ?? 0,
    tokens: 0,
    tools: 0,
    wakes: 0,
  };
  for (const row of rows) {
    if (row.event.type === "tool_call") {
      summary.tools += 1;
    }
    if (row.event.type === "activity_signal" && row.event.label === "WAKE") {
      summary.wakes += 1;
    }
    if (row.event.type === "token_delta") {
      summary.tokens +=
        row.event.inputTokensDelta + row.event.outputTokensDelta;
      summary.costUsd += row.event.costDeltaUsd;
    }
  }
  return summary;
}

function formatTokens(count: number): string {
  return count >= 1000 ? `${(count / 1000).toFixed(1)}k tok` : `${count} tok`;
}

function formatClock(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isStale(block: Extract<TimelineBlock, { kind: "turn" }>): boolean {
  const lastRow = block.rows[block.rows.length - 1];
  return (
    lastRow !== undefined && Date.now() - lastRow.timestamp > IN_FLIGHT_STALE_MS
  );
}

function blockEndTimestamp(block: TimelineBlock): number | null {
  if (block.kind === "turn") {
    return block.rows[block.rows.length - 1]?.timestamp ?? null;
  }
  return block.entry.timestamp;
}

function blockStartTimestamp(block: TimelineBlock): number {
  if (block.kind === "turn") {
    return block.rows[0]?.timestamp ?? 0;
  }
  return block.entry.timestamp;
}

function formatMetricParts(summary: TurnSummary): string[] {
  const parts: string[] = [];
  if (summary.tools > 0) {
    parts.push(`${summary.tools} ${summary.tools === 1 ? "tool" : "tools"}`);
  }
  if (summary.tokens > 0) {
    parts.push(formatTokens(summary.tokens));
  }
  if (summary.costUsd > 0) {
    parts.push(`$${summary.costUsd.toFixed(4)}`);
  }
  return parts;
}

function formatCapsuleSummary(
  summary: TurnSummary,
  rows: TimelineActivityEntry[],
): string {
  // Small metric-less capsules ARE their rows — narrate them instead of the
  // uninformative "2 events". Aggregates carry the receipt plus the story's
  // shape: wake count when the beat spans several, and how it ended.
  const metricParts = formatMetricParts(summary);
  if (metricParts.length === 0 && rows.length <= 3) {
    return rows.map((row) => row.label).join(" · ");
  }
  const parts: string[] = [];
  if (summary.wakes > 1) {
    parts.push(`${summary.wakes} wakes`);
  }
  parts.push(...metricParts);
  if (parts.length === 0) {
    parts.push(`${summary.events} events`);
  }
  if (summary.terminal) {
    parts.push(summary.terminal);
  }
  return parts.join(" · ");
}

// --- component ----------------------------------------------------------------

export const ChannelTimeline: FC<ChannelTimelineProps> = ({
  actorAttributions,
  appendLocalUserMessage,
  channelId,
  draft,
  events,
  hasMore,
  liveSince,
  loadMore,
  loadingMore,
  members,
  messages,
  onDraftChange,
  onSendMessage,
  readOnly = false,
}) => {
  const [input, setInput] = useState(draft);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Per-turn expand/collapse the user has explicitly chosen; wins over the
  // in-flight default. Keyed by block id (stable — first row's entry id).
  const [turnToggles, setTurnToggles] = useState<ReadonlyMap<string, boolean>>(
    new Map(),
  );
  // Periodic tick so an in-flight capsule can go stale (fold to its summary)
  // even when no further entries arrive to trigger a render.
  const [, setStaleTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setStaleTick((tick) => tick + 1), 30_000);
    return () => clearInterval(timer);
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Ref, not state: avoids a re-render cycle. The auto-scroll effect reads
  // this ref; onScroll updates it. If it were state, the smooth-scroll
  // animation would fire intermediate onScroll events that flip it false
  // mid-animation, re-triggering the effect and fighting the scroll.
  const pinnedToBottomRef = useRef(true);

  useEffect(() => {
    setInput(draft);
  }, [channelId, draft]);

  useEffect(() => {
    onDraftChange(input);
  }, [input, onDraftChange]);

  const entries = useMemo(
    () =>
      buildTimelineEntries({ actorAttributions, events, liveSince, messages }),
    [actorAttributions, events, liveSince, messages],
  );
  const blocks = useMemo(() => groupTimelineBlocks(entries), [entries]);
  // Index of the last message/boundary — everything after it is the trailing
  // telemetry run, where capsules may be live.
  const lastAnchorIndex = useMemo(() => {
    for (let index = blocks.length - 1; index >= 0; index -= 1) {
      if (blocks[index]?.kind !== "turn") {
        return index;
      }
    }
    return -1;
  }, [blocks]);

  // Auto-scroll to bottom when new entries arrive, but only if the user is
  // already pinned to the bottom. If they've scrolled up to read history,
  // leave them there — don't yank them down.
  useEffect(() => {
    if (!pinnedToBottomRef.current) {
      return;
    }
    const element = scrollRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [entries.length]);

  const handleScroll = () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const atBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 40;
    pinnedToBottomRef.current = atBottom;
  };

  const handleToggleTurn = useCallback((blockId: string, expanded: boolean) => {
    setTurnToggles((previous) => {
      const next = new Map(previous);
      next.set(blockId, expanded);
      return next;
    });
  }, []);

  const handleInsertMention = useCallback((displayName: string) => {
    setInput((previous) => {
      const separator =
        previous.length === 0 || previous.endsWith(" ") ? "" : " ";
      return `${previous}${separator}@${displayName} `;
    });
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, []);

  const handleSend = async () => {
    if (sending) {
      return;
    }
    pinnedToBottomRef.current = true;
    setSending(true);
    setSendError(null);
    const result = await submitTimelineMessage({
      appendLocalUserMessage,
      channelId,
      draft: input,
      onSendMessage,
    });
    setInput(result.draft);
    setSendError(result.error);
    setSending(false);
  };

  const textareaRows = Math.min(4, Math.max(1, input.split("\n").length));

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="native-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-3"
      >
        {hasMore && (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="mx-auto mb-3 block font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-emerald-500 disabled:opacity-50 dark:text-slate-500"
          >
            {loadingMore ? "loading…" : "▸ load older messages"}
          </button>
        )}
        {entries.length === 0 && (
          <p className="py-8 text-center font-mono text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
            awaiting signal — messages and activity stream when the channel is
            hot
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          {blocks.map((block, index) => {
            const previous = blocks[index - 1];
            const previousEnd =
              previous === undefined ? null : blockEndTimestamp(previous);
            const blockStart = blockStartTimestamp(block);
            const showTimeRule =
              previousEnd !== null &&
              blockStart - previousEnd > TIMESTAMP_GAP_MS;
            // In-flight is per capsule within the trailing telemetry run (the
            // blocks after the last message/boundary): with turn-coalescing,
            // two concurrent actors can both be live — both pulse.
            const inTrailingRun = index > lastAnchorIndex;
            const inFlight =
              block.kind === "turn" &&
              inTrailingRun &&
              !block.endsTurn &&
              !isStale(block);
            return (
              <div key={block.id}>
                {showTimeRule && <TimeRule timestamp={blockStart} />}
                {block.kind === "message" && (
                  <MessageBlock entry={block.entry} />
                )}
                {block.kind === "boundary" && (
                  <LiveBoundary timestamp={block.entry.timestamp} />
                )}
                {block.kind === "turn" && (
                  <TurnBlock
                    block={block}
                    expanded={turnToggles.get(block.id) ?? inFlight}
                    inFlight={inFlight}
                    onToggle={handleToggleTurn}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {!readOnly && (
        <div className="border-t border-slate-200 p-2 dark:border-slate-800">
          {sendError && (
            <p className="mb-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[--color-danger]">
              send failed — draft restored
            </p>
          )}
          <div className="flex items-end gap-2">
            {members.length > 0 && (
              <MentionMenu members={members} onSelect={handleInsertMention} />
            )}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
              rows={textareaRows}
              placeholder="message #channel…"
              className="min-w-0 flex-1 resize-none border border-slate-300 bg-white/60 px-2 py-1.5 font-mono text-[12px] text-slate-800 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200"
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={sending || !input.trim()}
              className="shrink-0 border border-emerald-500 bg-emerald-500/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-40 dark:text-emerald-300"
            >
              {sending ? "···" : "send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- rules + boundaries -------------------------------------------------------

/** Hairline time rule: reads as chrome, not content — line left, time right. */
const TimeRule: FC<{ timestamp: number }> = ({ timestamp }) => (
  <div
    className="mb-1.5 flex items-center gap-2"
    title={new Date(timestamp).toISOString()}
  >
    <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
    <span className="font-mono text-[9px] uppercase tabular-nums tracking-widest text-slate-400 dark:text-slate-500">
      {formatClock(timestamp)}
    </span>
    <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
  </div>
);

/**
 * The honest seam: full message history above, live-only telemetry below.
 * Rendered as an explicit labelled boundary so the absence of older activity
 * reads as a window edge, not as "the actors were idle."
 */
const LiveBoundary: FC<{ timestamp: number }> = ({ timestamp }) => (
  <div
    className="my-2 flex items-center gap-2"
    title={new Date(timestamp).toISOString()}
  >
    <div className="h-px flex-1 bg-emerald-500/25" />
    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-emerald-600/70 dark:text-emerald-400/60">
      live telemetry from {formatClock(timestamp)}
    </span>
    <div className="h-px flex-1 bg-emerald-500/25" />
  </div>
);

// --- messages -------------------------------------------------------------------

/**
 * Speech is figural. The sender + time meta line sits OUTSIDE the bubble so
 * the bubble is pure speech; the avatar sits in the left gutter (the spine
 * everything else hangs from). Operator messages keep the chat convention:
 * right-aligned, emerald. System notices join the telemetry tier as a quiet
 * SYS row instead of a third centered regime.
 *
 * Message bodies use `overflow-wrap: anywhere`, not `break-word`: only
 * `anywhere` reduces the paragraph's MIN-CONTENT width. Without it a single
 * unbroken token — a fully-qualified test name in backticks, a URL, a path —
 * sets the column's intrinsic minimum, the bubble outgrows its max-width, and
 * the pane scrolls sideways with the start of the line clipped off. Normal
 * word wrapping is unaffected; `anywhere` only breaks where nothing else can.
 */
const MessageBlock: FC<{ entry: TimelineMessageEntry }> = ({ entry }) => {
  const { identity, message } = entry;
  const isUser = message.senderType === "user";
  const isSystem = message.senderType === "system";
  const time = formatClock(entry.timestamp);
  const isoTime = new Date(entry.timestamp).toISOString();

  if (isSystem) {
    return (
      <div className="flex items-center gap-1.5 pl-9" title={isoTime}>
        <span className="border border-slate-300 px-1 font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:border-slate-700 dark:text-slate-500">
          sys
        </span>
        <span className="min-w-0 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
          {message.content}
        </span>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="my-1 flex flex-col items-end" title={isoTime}>
        <p className="mb-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-700/70 dark:text-emerald-300/60">
          you{" "}
          <span className="tabular-nums text-emerald-700/50 dark:text-emerald-300/40">
            · {time}
          </span>
        </p>
        <div className="min-w-0 max-w-[78%] border border-emerald-400/50 bg-emerald-400/10 px-3 py-2 text-emerald-900 shadow-[3px_3px_0_rgba(16,185,129,0.12)] dark:text-emerald-50 dark:shadow-[3px_3px_0_rgba(0,0,0,0.35)]">
          <ChatMessageMarkdown content={message.content} />
        </div>
      </div>
    );
  }

  return (
    <div className="my-1 flex gap-2" title={isoTime}>
      <div className="w-7 shrink-0 pt-4">
        <InstanceAvatar seed={identity.avatarSeed} size="sm" animated />
      </div>
      <div className="min-w-0 flex-1">
        <p className="mb-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
          {identity.displayName}{" "}
          <span className="tabular-nums text-slate-400/80 dark:text-slate-500/80">
            · {time}
          </span>
        </p>
        <div className="w-fit min-w-0 max-w-[85%] border border-slate-300 bg-white/60 px-3 py-2 text-slate-800 shadow-[3px_3px_0_rgba(15,23,42,0.08)] dark:border-slate-700/60 dark:bg-slate-900/60 dark:text-slate-100 dark:shadow-[3px_3px_0_rgba(0,0,0,0.35)]">
          <ChatMessageMarkdown content={message.content} />
        </div>
      </div>
    </div>
  );
};

// --- telemetry -------------------------------------------------------------------

/**
 * A turn capsule. Collapsed: one summarizable row — the actor worked, here's
 * the receipt (tools · tokens · cost · time range). Expanded: the full trace,
 * rows hanging off a rail tinted with the actor's avatar color so ownership
 * reads by position, not just hue. The live in-flight turn streams expanded
 * with a pulse and folds when the actor parks.
 */
const TurnBlock: FC<{
  block: Extract<TimelineBlock, { kind: "turn" }>;
  expanded: boolean;
  inFlight: boolean;
  onToggle: (blockId: string, expanded: boolean) => void;
}> = ({ block, expanded, inFlight, onToggle }) => {
  const { identity, rows } = block;
  const summary = useMemo(() => summarizeTurn(rows), [rows]);
  const tint = identity ? getAvatarBackgroundColor(identity.avatarSeed) : null;
  const fromClock = formatClock(summary.from);
  const toClock = formatClock(summary.to);
  const timeRange =
    fromClock === toClock ? fromClock : `${fromClock}–${toClock}`;

  return (
    <div className={block.endsTurn ? "mb-2" : undefined}>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => onToggle(block.id, !expanded)}
        className="group flex w-full items-center gap-1.5 py-0.5 pl-1 text-left"
        title={`${new Date(summary.from).toISOString()} — ${rows.length} events`}
      >
        <span className="w-2 shrink-0 text-center font-mono text-[9px] text-slate-400 dark:text-slate-500">
          {inFlight ? (
            <span className="mx-auto block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
          ) : (
            <span>{expanded ? "▾" : "▸"}</span>
          )}
        </span>
        {identity ? (
          <>
            <InstanceAvatar seed={identity.avatarSeed} size="xs" />
            <span className="shrink-0 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-slate-200">
              {identity.displayName}
            </span>
          </>
        ) : (
          <span className="shrink-0 border border-slate-300 px-1 font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:border-slate-700 dark:text-slate-500">
            sys
          </span>
        )}
        <span className="min-w-0 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
          {inFlight
            ? ["working…", ...formatMetricParts(summary)].join(" · ")
            : formatCapsuleSummary(summary, rows)}
        </span>
        <span className="ml-auto shrink-0 font-mono text-[9px] tabular-nums text-slate-400/80 dark:text-slate-500/80">
          {timeRange}
        </span>
      </button>
      {expanded && (
        <div
          className="ml-[7px] flex flex-col gap-px border-l-2 pl-3"
          style={{
            borderColor: tint ? `${tint}66` : "rgb(100 116 139 / 0.25)",
          }}
        >
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-baseline gap-2"
              title={new Date(row.timestamp).toISOString()}
            >
              <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
                {row.label}
              </span>
              <span className="shrink-0 font-mono text-[9px] tabular-nums text-slate-400/60 dark:text-slate-500/60">
                {formatClock(row.timestamp)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const __testing = {
  buildTimelineEntries,
  formatCapsuleSummary,
  groupTimelineBlocks,
  submitTimelineMessage,
  summarizeTurn,
};
